// Label and Budget Management Types

// Label Definition
export interface LabelDefinition {
  id: string;                // UUID
  name: string;              // Label name (e.g., Name, Title, Department)
  order: number;             // Display order
  type: 'text' | 'select' | 'number' | 'currency' | 'employee';  // Input type
  options?: string[];        // Options for select type
  employeeField?: 'name' | 'email' | 'department' | 'role' | 'employeeId' | 'phone' | 'joinDate';  // For employee type
  createdAt: string;
  updatedAt: string;
}

// API Key Metadata
export interface ApiKeyMetadata {
  apiKeyId: string;          // OpenAI API Key ID
  apiKeyName: string;        // API Key name (for caching)
  labels: {
    [labelId: string]: string | number;
  };
  budgetLimit?: number;      // Monthly budget limit (JPY)
  budgetPeriod?: 'monthly' | 'daily' | 'weekly';
  budgetActions?: {
    warningThreshold: number;  // Warning threshold (%)
    autoStop: boolean;         // Auto-stop enabled
    notifyEmail?: string;      // Notification email
  };
  status?: 'active' | 'stopped' | 'budget_exceeded';
  lastStoppedAt?: string;
  lastRestartedAt?: string;
  updatedAt: string;
}

// Budget Tracking Data
export interface BudgetTracking {
  apiKeyId: string;
  period: string;            // '2025-07' for monthly
  usage: number;             // Current usage amount
  limit: number;             // Set limit
  percentage: number;        // Usage percentage
  projectedUsage: number;    // Month-end projected amount
  lastCheckedAt: string;
  actions: {
    warningsSent: string[];  // Warning sent timestamps
    stopExecutedAt?: string; // Stop execution timestamp
  };
}

// Budget Configuration
export interface BudgetConfig {
  limit: number;
  period: 'monthly' | 'daily' | 'weekly';
  warningThreshold: number;  // 70%, 80%, 90%
  autoStop: boolean;
  notifyEmail?: string;
}

// Preset Templates for Label Definitions
export const PRESET_TEMPLATES = {
  corporate: [
    { name: 'Name', type: 'text' as const },
    { name: 'Title', type: 'select' as const, options: ['Manager', 'Director', 'Lead', 'Senior', 'Junior'] },
    { name: 'Department', type: 'select' as const, options: ['Sales', 'Development', 'Finance', 'HR'] },
    { name: 'Project', type: 'text' as const },
    { name: 'Budget Limit', type: 'currency' as const }
  ],
  startup: [
    { name: 'Member Name', type: 'text' as const },
    { name: 'Team', type: 'text' as const },
    { name: 'Role', type: 'select' as const, options: ['Engineer', 'Designer', 'PM', 'QA'] },
    { name: 'Project', type: 'text' as const },
    { name: 'Monthly Limit', type: 'currency' as const }
  ]
};

// Storage Keys
export const STORAGE_KEYS = {
  LABEL_DEFINITIONS: 'openai-dashboard-label-definitions',
  API_KEY_METADATA: 'openai-dashboard-api-key-metadata',
  BUDGET_TRACKING: 'openai-dashboard-budget-tracking'
} as const;

// Storage Data Structures
export interface LabelDefinitionsStorage {
  version: string;
  definitions: LabelDefinition[];
  lastUpdated: string;
}

export interface ApiKeyMetadataStorage {
  version: string;
  metadata: ApiKeyMetadata[];
  lastUpdated: string;
}

export interface BudgetTrackingStorage {
  version: string;
  tracking: BudgetTracking[];
  lastUpdated: string;
}